import React, { useEffect, useState } from "react";
import { Input, Modal } from "antd";
import { X } from "react-feather";

import { AppStrings } from "../../../../utils/constants/AppStrings";
import { formatString } from "../../../../utils/constants/stringUtils";
import type { SportsbookTranslation } from "../../../../queries/sportsbook-fm/sportsbookTranslations";

import { TranslationField } from "./TranslationField";
import { useSportsbookTranslationEditor } from "./useSportsbookTranslationEditor";
import "./stylesTranslations.css";

export type BundledTranslationChanges = {
  translation_key: string;
  translations: Record<string, string>;
};

export interface RenameEntityModalProps {
  open: boolean;
  title: string;
  nameLabel: string;
  namePlaceholder?: string;
  initialName: string;
  entityKey: string | undefined;
  initialTranslationData?: SportsbookTranslation;
  translationsEnabled: boolean;
  /**
   * When true, dirty locale drafts ride along in `onSave` so the caller's own
   * rename request carries them. Leave it off only for entities whose rename
   * endpoint cannot accept `translation_key`/`translations`.
   */
  bundleTranslationChanges?: boolean;
  saving: boolean;
  onSave: (
    name: string,
    translationChanges?: BundledTranslationChanges,
  ) => Promise<void> | void;
  /**
   * Locales the per-locale endpoint accepted, so the caller's list row can show
   * them without a refetch. Not called for bundled changes — those reach the
   * caller through `onSave`.
   */
  onTranslationsSaved?: (changes: BundledTranslationChanges) => void;
  onClose: () => void;
}

export function RenameEntityModal({
  open,
  title,
  nameLabel,
  namePlaceholder,
  initialName,
  entityKey,
  initialTranslationData,
  translationsEnabled,
  bundleTranslationChanges = false,
  saving,
  onSave,
  onTranslationsSaved,
  onClose,
}: RenameEntityModalProps): React.ReactElement {
  const [name, setName] = useState(initialName);
  const editor = useSportsbookTranslationEditor({
    entityKey: open ? entityKey : undefined,
    enabled: translationsEnabled,
    initialData: initialTranslationData,
  });
  const { reset } = editor;

  useEffect(() => {
    if (open) {
      setName(initialName);
    } else {
      reset();
    }
  }, [initialName, open, reset]);

  const close = (allowDirtyTranslations = false) => {
    if (saving || editor.isMutating) return;
    if (editor.hasDirtyTranslations && !allowDirtyTranslations) {
      editor.showUnsavedChangesError();
      return;
    }
    editor.reset();
    onClose();
  };

  const save = async () => {
    const trimmedName = name.trim();
    if (
      !trimmedName ||
      saving ||
      editor.isMutating ||
      editor.hasEmptyTranslationDraft
    ) {
      return;
    }

    const hadDirtyTranslations = editor.hasDirtyTranslations;
    const nameChanged = trimmedName !== initialName.trim();
    const dirtyValues = editor.dirtyTranslationValues;
    const translationChanges =
      bundleTranslationChanges &&
      editor.entityKey &&
      Object.keys(dirtyValues).length > 0
        ? {
            translation_key: editor.entityKey,
            translations: dirtyValues,
          }
        : undefined;

    if (!bundleTranslationChanges) {
      const savedKey = editor.entityKey;
      if (!(await editor.saveTranslations())) return;
      if (savedKey && Object.keys(dirtyValues).length > 0) {
        onTranslationsSaved?.({
          translation_key: savedKey,
          translations: dirtyValues,
        });
      }
    }

    if (nameChanged || translationChanges) {
      if (translationChanges) {
        await onSave(trimmedName, translationChanges);
      } else {
        await onSave(trimmedName);
      }
      return;
    }

    close(hadDirtyTranslations);
  };

  const saveDisabled =
    !name.trim() ||
    saving ||
    editor.isMutating ||
    editor.hasEmptyTranslationDraft;

  return (
    <Modal
      closable={false}
      open={open}
      onCancel={() => close()}
      footer={null}
      width={480}
      className="rename-modal future-tab-edit-modal"
    >
      <div className="add-new-ip-panel future-tab-edit-panel">
        <div className="modal-header">
          <div className="modal-header-title">
            <h2 className="modal-title">{title}</h2>
          </div>
          <X
            onClick={() => close()}
            className="close-icon"
            aria-label={AppStrings.SPORTSBOOK_FM_CLOSE}
          />
        </div>
        <div className="divider" />
        <div className="msb-modal-body">
          <div className="future-tab-edit-field">
            <label htmlFor="renameInput">{nameLabel}</label>
            <Input
              id="renameInput"
              placeholder={namePlaceholder ?? nameLabel}
              value={name}
              onChange={(event) => setName(event.target.value)}
              onPressEnter={save}
            />
          </div>
          <TranslationField editor={editor} />
        </div>
        <div className="modal-footer">
          <button
            className="cancel-button"
            onClick={() => close()}
            disabled={saving || editor.isMutating}
          >
            {AppStrings.SPORTSBOOK_FM_CLOSE}
          </button>
          <button
            className={`submit-button ${saveDisabled ? "disabled" : ""}`}
            disabled={saveDisabled}
            aria-label={
              saving || editor.isMutating
                ? AppStrings.SAVING
                : editor.selectedLocale
                  ? formatString(
                      AppStrings.SPORTSBOOK_FM_SAVE_TRANSLATION_ARIA,
                      { locale: editor.selectedLocale },
                    )
                  : AppStrings.SAVE
            }
            onClick={save}
          >
            {saving || editor.isMutating
              ? AppStrings.SAVING
              : AppStrings.SAVE}
          </button>
        </div>
      </div>
    </Modal>
  );
}
